import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { sanitizeFreeText } from "./sanitize";
import { openingBuyNowCost, type Urgency } from "./credit-pricing";
import { describeMeasurements, estimateJobValue } from "./estimate";
import { sendNotification } from "./notifications.server";
import { isCanonicalServiceSlug } from "./service-categories";

const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

const bookingSchema = z.object({
  homeId: z.string().uuid().optional().nullable(), serviceId: z.string().uuid(), description: z.string().min(20).max(4000), propertyType: z.string().min(1).max(60),
  maintenanceCondition: z.string().max(60).optional().nullable(), sizeEstimate: z.string().max(60).optional().nullable(),
  lengthM: z.number().positive().max(10000).optional().nullable(), widthM: z.number().positive().max(10000).optional().nullable(), heightM: z.number().positive().max(1000).optional().nullable(),
  dimensionUnit: z.enum(["m", "ft"]).default("m"), accessNotes: z.string().max(2000).optional().nullable(), hazards: z.string().max(2000).optional().nullable(),
  pricingMode: z.enum(["brief", "onsite"]).default("brief"), addressLine: z.string().max(200).optional().nullable(), townCity: z.string().max(100).optional().nullable(),
  postcode: z.string().regex(UK_POSTCODE, "Enter a valid UK postcode"), preferredDate: z.string().date(), preferredWindow: z.string().trim().min(1).max(60),
  altDate: z.string().date().optional().nullable(), altWindow: z.string().max(60).optional().nullable(), urgency: z.enum(["standard", "urgent"]).default("standard"),
  adultConfirmed: z.boolean().refine((v) => v === true, "You must confirm you're 18 or over"), idempotencyKey: z.string().min(8).max(80),
}).superRefine((value, ctx) => {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  if (value.preferredDate && value.preferredDate < today) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["preferredDate"], message: "Choose today or a future date" });
  }
  if (value.altDate && value.altDate < today) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["altDate"], message: "Choose today or a future date" });
  }
  if (value.preferredWindow && !value.preferredDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["preferredWindow"], message: "Choose a preferred date with this time window" });
  }
  if (value.altWindow && !value.altDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["altWindow"], message: "Choose an alternative date with this time window" });
  }
  if (value.preferredDate && value.altDate && value.preferredDate === value.altDate && (value.preferredWindow || "") === (value.altWindow || "")) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["altDate"], message: "Choose a different alternative date or time" });
  }
});

export const createBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => bookingSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: existing, error: existingError } = await supabase
      .from("bookings")
      .select("id,reference,status,customer_id")
      .eq("idempotency_key", data.idempotencyKey)
      .eq("customer_id", userId)
      .maybeSingle();
    if (existingError) throw new Error(existingError.message);
    if (existing) {
      // A retry after a network interruption must return the already-created
      // booking instead of creating another request.
      let matchingIssue = existing.status === "manual_matching";
      if (existing.status === "matching") {
        try {
          await dispatchOffers(existing.id);
        } catch (error) {
          matchingIssue = true;
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          await supabaseAdmin.from("operational_alerts").insert({
            severity: "warning",
            title: "Booking matching retry failed",
            detail: {
              booking_id: existing.id,
              reference: existing.reference,
              error: error instanceof Error ? error.message : String(error),
            },
          });
        }
      }
      return {
        ok: true as const,
        id: existing.id,
        reference: existing.reference,
        duplicate: true,
        matchingIssue,
      };
    }

    const { data: service, error: serviceError } = await supabase
      .from("services")
      .select("id,name,slug,active")
      .eq("id", data.serviceId)
      .eq("active", true)
      .maybeSingle();
    if (serviceError) throw new Error(serviceError.message);
    if (!service || !isCanonicalServiceSlug(service.slug)) {
      throw new Error("That service is not currently available");
    }

    let selectedHome: {
      id: string;
      address_line: string | null;
      town_city: string | null;
      postcode: string;
    } | null = null;
    if (data.homeId) {
      const { data: home, error: homeError } = await supabase
        .from("saved_homes")
        .select("id,address_line,town_city,postcode")
        .eq("id", data.homeId)
        .eq("user_id", userId)
        .maybeSingle();
      if (homeError) throw new Error(homeError.message);
      if (!home) throw new Error("The selected property could not be found");
      selectedHome = home;
    }

    const effectivePostcode = (selectedHome?.postcode ?? data.postcode).toUpperCase();
    const effectiveAddress = selectedHome?.address_line ?? data.addressLine ?? null;
    const effectiveTown = selectedHome?.town_city ?? data.townCity ?? null;

    const { enforceRateLimit } = await import("./rate-limit.server");
    const rl = await enforceRateLimit(supabase, {
      action: "create_booking",
      maxEvents: 5,
      windowSeconds: 3600,
    });
    if (!rl.allowed) throw new Error(rl.error);

    const { data: booking, error } = await supabase
      .from("bookings")
      .insert({
        customer_id: userId,
        home_id: selectedHome?.id ?? null,
        service_id: data.serviceId,
        status: "matching",
        description: data.description,
        property_type: data.propertyType,
        maintenance_condition: data.maintenanceCondition ?? null,
        size_estimate: data.sizeEstimate ?? null,
        length_m: data.lengthM ?? null,
        width_m: data.widthM ?? null,
        height_m: data.heightM ?? null,
        dimension_unit: data.dimensionUnit,
        access_notes: data.accessNotes ?? null,
        hazards: data.hazards ?? null,
        pricing_mode: data.pricingMode,
        address_line: effectiveAddress,
        town_city: effectiveTown,
        postcode: effectivePostcode,
        preferred_date: data.preferredDate,
        preferred_window: data.preferredWindow,
        alt_date: data.altDate || null,
        alt_window: data.altWindow ?? null,
        adult_confirmed: data.adultConfirmed,
        urgency: data.urgency,
        auction_ends_at: new Date(Date.now() + 24 * 3_600_000).toISOString(),
        idempotency_key: data.idempotencyKey,
        reference: "",
      })
      .select("id,reference")
      .single();
    if (error) throw new Error(error.message);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const admin = supabaseAdmin as any;
    const outward = effectivePostcode.trim().split(/\s+/)[0] ?? null;
    const publicDescription = sanitizeFreeText(data.description).slice(0, 600);

    // Secondary records must not make a successfully-created booking appear to
    // have failed. Any failure is logged for operations while the customer keeps
    // the real booking record.
    const sideEffectErrors: string[] = [];
    const recordSideEffect = async (label: string, work: () => Promise<unknown>) => {
      try {
        await work();
      } catch (sideEffectError) {
        sideEffectErrors.push(
          `${label}: ${sideEffectError instanceof Error ? sideEffectError.message : String(sideEffectError)}`,
        );
      }
    };

    await recordSideEffect("booking event", async () => {
      const { error: eventError } = await admin.from("booking_events").insert({
        booking_id: booking.id,
        actor_id: userId,
        event_type: "booking_submitted",
        detail: { reference: booking.reference },
      });
      if (eventError) throw new Error(eventError.message);
    });

    await recordSideEffect("agent event", async () => {
      const { emitAgentEvent } = await import("./agents.server");
      await emitAgentEvent({
        eventType: "customer_job_created",
        idempotencyKey: `customer_job_created:${booking.id}`,
        payload: { booking_id: booking.id, reference: booking.reference },
        source: "booking",
        actorId: userId,
      });
    });

    await recordSideEffect("customer notification", async () => {
      await sendNotification(admin, {
        recipientId: userId,
        template: "booking_confirmed",
        payload: { booking_id: booking.id, reference: booking.reference },
      });
    });

    await recordSideEffect("marketplace post", async () => {
      const { error: postError } = await admin.from("posts").insert({
        author_id: userId,
        kind: "job",
        media_type: "none",
        title: service.name ?? "Job wanted",
        body: publicDescription,
        area_label: outward,
        urgency: data.urgency,
        service_id: data.serviceId,
        booking_id: booking.id,
      });
      if (postError) throw new Error(postError.message);
    });

    let matchingIssue = false;
    try {
      await dispatchOffers(booking.id);
    } catch (matchingError) {
      matchingIssue = true;
      await admin
        .from("bookings")
        .update({ status: "manual_matching" })
        .eq("id", booking.id)
        .eq("customer_id", userId);
      sideEffectErrors.push(
        `matching: ${matchingError instanceof Error ? matchingError.message : String(matchingError)}`,
      );
    }

    if (sideEffectErrors.length) {
      await admin.from("operational_alerts").insert({
        severity: matchingIssue ? "warning" : "info",
        title: matchingIssue
          ? "Booking created but matching needs review"
          : "Booking created with secondary follow-up required",
        detail: {
          booking_id: booking.id,
          reference: booking.reference,
          errors: sideEffectErrors,
        },
      });
    }

    return {
      ok: true as const,
      id: booking.id,
      reference: booking.reference,
      duplicate: false,
      matchingIssue,
    };
  });

export async function dispatchOffers(bookingId: string, opts?: { wave?: number }) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { rankPartnersForBooking, wavePlan, geocodePostcode, RADIUS_STRETCH_MILES } = await import("./matching.server");
  const { data: booking, error: bookingError } = await supabaseAdmin.from("bookings").select("*, services(name, slug)").eq("id", bookingId).maybeSingle();
  if (bookingError) throw new Error(`Unable to load booking for matching: ${bookingError.message}`);
  if (!booking || booking.assigned_partner_id) return { dispatched: 0 };
  const serviceSlug = (booking.services as { slug?: string } | null)?.slug ?? "";
  const serviceName = (booking.services as { name?: string } | null)?.name ?? "Service";
  const measurements = describeMeasurements(booking); const estimate = estimateJobValue(booking.size_estimate, booking.maintenance_condition, booking);
  const urgency = ((booking as { urgency?: string }).urgency ?? "standard") as Urgency; const getNowCredits = openingBuyNowCost(estimate.mid, urgency);
  let lat = (booking as { lat?: number | null }).lat ?? null; let lng = (booking as { lng?: number | null }).lng ?? null;
  if (lat == null || lng == null) { const geo = await geocodePostcode(booking.postcode); if (geo) { lat = geo.lat; lng = geo.lng; await supabaseAdmin.from("bookings").update({ lat, lng }).eq("id", bookingId); } }
  let stretched = false;
  let ranked = await rankPartnersForBooking(supabaseAdmin, {
    id: bookingId,
    customerId: booking.customer_id,
    lat,
    lng,
    serviceSlug,
    creditsNeeded: getNowCredits,
    preferredDate: booking.preferred_date,
    preferredWindow: booking.preferred_window,
    altDate: booking.alt_date,
    altWindow: booking.alt_window,
  });
  if (ranked.length === 0) { stretched = true; ranked = await rankPartnersForBooking(supabaseAdmin, {
      id: bookingId,
      customerId: booking.customer_id,
      lat,
      lng,
      serviceSlug,
      creditsNeeded: getNowCredits,
      radiusStretch: RADIUS_STRETCH_MILES,
      preferredDate: booking.preferred_date,
      preferredWindow: booking.preferred_window,
      altDate: booking.alt_date,
      altWindow: booking.alt_window,
    }); }
  if (ranked.length === 0) {
    const { data: updatedBooking, error: statusError } = await supabaseAdmin
      .from("bookings")
      .update({ status: "manual_matching" })
      .eq("id", bookingId)
      .in("status", ["matching", "bidding"])
      .is("assigned_partner_id", null)
      .select("id")
      .maybeSingle();
    if (statusError) throw new Error(`Unable to mark booking for manual matching: ${statusError.message}`);
    // Another worker may have assigned or closed the job while ranking ran.
    if (!updatedBooking) return { dispatched: 0 };

    const { error: alertError } = await supabaseAdmin.from("operational_alerts").insert({
      severity: "warning",
      title: "No eligible partner for booking",
      detail: { booking_id: bookingId, reference: booking.reference, service: serviceSlug },
    });
    if (alertError) throw new Error(`Unable to alert operations about unmatched booking: ${alertError.message}`);
    return { dispatched: 0 };
  }
  const wave = Math.min(opts?.wave ?? ((booking as { dispatch_wave?: number }).dispatch_wave ?? 0) + 1, 3); const plan = wavePlan(wave); const chosen = ranked.slice(0, Math.min(plan.take, ranked.length));
  const { data: alreadyOffered, error: offeredError } = await supabaseAdmin.from("job_offers").select("partner_id").eq("booking_id", bookingId); if (offeredError) throw new Error(`Unable to read existing job offers: ${offeredError.message}`); const known = new Set((alreadyOffered ?? []).map((o) => o.partner_id as string)); const fresh = chosen.filter((c) => !known.has(c.partnerId));
  const rows = chosen.map((c) => ({ booking_id: bookingId, partner_id: c.partnerId, service_name: serviceName, scope_summary: sanitizeFreeText(booking.description).slice(0, 800) + (measurements ? `\n\nMeasurements: ${measurements}` : "\n\nMeasurements: not supplied by the customer"), outward_postcode: booking.outward_postcode, property_type: booking.property_type, maintenance_condition: booking.maintenance_condition, size_estimate: booking.size_estimate, access_notes: sanitizeFreeText(booking.access_notes), hazards: sanitizeFreeText(booking.hazards), preferred_date: booking.preferred_date, preferred_window: booking.preferred_window, estimate_low_pence: estimate.low, estimate_high_pence: estimate.high, get_now_credits: getNowCredits }));
  const { error: offerError } = await supabaseAdmin.from("job_offers").upsert(rows, { onConflict: "booking_id,partner_id" });
  if (offerError) throw new Error(`Unable to save job offers: ${offerError.message}`);
  const { data: updatedBooking, error: statusError } = await supabaseAdmin.from("bookings").update({ status: "bidding", dispatch_wave: wave, wave_expires_at: new Date(Date.now() + plan.minutes * 60_000).toISOString(), ...(stretched ? { dispatch_radius_miles: RADIUS_STRETCH_MILES } : {}) }).eq("id", bookingId).in("status", ["matching", "bidding"]).is("assigned_partner_id", null).select("id").maybeSingle();
  if (statusError || !updatedBooking) throw new Error(`Unable to mark booking as bidding: ${statusError?.message ?? "booking changed during matching"}`);
  await supabaseAdmin.from("booking_events").insert({ booking_id: bookingId, event_type: "offers_dispatched", detail: { wave, count: rows.length, new_offers: fresh.length, eligible: ranked.length, radius_stretched: stretched, ranking: chosen.slice(0, 10).map((c) => ({ partner_id: c.partnerId, score: c.score, miles: c.distanceMiles == null ? null : Number(c.distanceMiles.toFixed(1)), why: c.reasons.join(", ") })) } });
  for (const c of fresh) await sendNotification(supabaseAdmin, { recipientId: c.partnerId, template: "new_job_offer", payload: { booking_id: bookingId, reference: booking.reference } });
  return { dispatched: fresh.length, offered: rows.length, wave, eligible: ranked.length };
}

export const recordBookingEvidence = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) =>
    z.object({
      bookingId: z.string().uuid(),
      path: z.string().trim().min(3).max(500),
      kind: z.enum(["photo", "video"]),
    }).parse(value),
  )
  .handler(async ({ data, context }) => {
    if (!data.path.startsWith(context.userId + "/")) {
      return { ok: false as const, error: "Invalid evidence file path" };
    }
    const { data: booking } = await context.supabase
      .from("bookings")
      .select("id,customer_id,status")
      .eq("id", data.bookingId)
      .maybeSingle();
    if (!booking || booking.customer_id !== context.userId) {
      return { ok: false as const, error: "Booking not found" };
    }
    if (["cancelled","declined","expired","closed"].includes(booking.status)) {
      return { ok: false as const, error: "This booking no longer accepts customer media" };
    }

    const { count } = await context.supabase
      .from("booking_evidence")
      .select("id", { count: "exact", head: true })
      .eq("booking_id", data.bookingId)
      .eq("uploader_id", context.userId);
    if ((count ?? 0) >= 8) {
      return { ok: false as const, error: "You can add up to 8 photos or videos to a booking" };
    }

    const { error } = await context.supabase.from("booking_evidence").insert({
      booking_id: data.bookingId,
      uploader_id: context.userId,
      kind: data.kind,
      image_path: data.path,
    });
    if (error) return { ok: false as const, error: error.message };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("booking_events").insert({
      booking_id: data.bookingId,
      actor_id: context.userId,
      event_type: "customer_media_added",
      detail: { kind: data.kind },
    });
    return { ok: true as const };
  });

async function signedBookingEvidence(bookingId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await (supabaseAdmin as any)
    .from("booking_evidence")
    .select("id,uploader_id,kind,image_path,created_at")
    .eq("booking_id", bookingId)
    .order("created_at");
  const rows = data ?? [];
  if (!rows.length) return [];
  const { signPhotoPaths } = await import("./photos.server");
  const urls = await signPhotoPaths("job-evidence", rows.map((row: any) => row.image_path));
  return rows.flatMap((row: any, index: number) =>
    urls[index]
      ? [{
          id: row.id,
          kind: row.kind as "photo" | "video",
          url: urls[index]!,
          createdAt: row.created_at,
        }]
      : [],
  );
}

export const getCustomerAttention = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const today = new Date();
    const soon = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);

    const [{ data: bookings }, { data: payments }, { data: reminders }] = await Promise.all([
      context.supabase
        .from("bookings")
        .select("id,reference,status,preferred_date,scheduled_date,services(name)")
        .eq("customer_id", context.userId)
        .in("status", ["bidding", "awaiting_customer_approval", "issue_open"])
        .order("updated_at", { ascending: false })
        .limit(20),
      context.supabase
        .from("job_payments")
        .select("id,booking_id,status,customer_total_pence,created_at")
        .eq("customer_id", context.userId)
        .in("status", ["payment_pending", "payment_failed"])
        .order("created_at", { ascending: false })
        .limit(20),
      context.supabase
        .from("home_service_reminders")
        .select("id,title,due_date,source,status")
        .eq("user_id", context.userId)
        .eq("status", "active")
        .lte("due_date", soon)
        .order("due_date")
        .limit(20),
    ]);

    const paymentByBooking = new Map((payments ?? []).map((payment) => [payment.booking_id, payment]));
    const items: Array<{
      id: string;
      kind: "booking" | "payment" | "reminder";
      title: string;
      detail: string;
      action: string;
      to: string;
      priority: number;
    }> = [];

    for (const booking of bookings ?? []) {
      const service = (booking.services as { name?: string } | null)?.name ?? "Service";
      if (booking.status === "bidding") {
        items.push({
          id: `booking:${booking.id}:bidding`,
          kind: "booking",
          title: "Choose a professional",
          detail: service,
          action: "Compare options",
          to: `/bookings/${booking.id}`,
          priority: 20,
        });
      } else if (booking.status === "awaiting_customer_approval") {
        items.push({
          id: `booking:${booking.id}:approval`,
          kind: "booking",
          title: "Confirm the work",
          detail: service,
          action: "Review completion",
          to: `/bookings/${booking.id}`,
          priority: 10,
        });
      } else if (booking.status === "issue_open") {
        items.push({
          id: `booking:${booking.id}:issue`,
          kind: "booking",
          title: "Job issue needs attention",
          detail: service,
          action: "Open job",
          to: `/bookings/${booking.id}`,
          priority: 5,
        });
      }
    }

    for (const payment of payments ?? []) {
      if (paymentByBooking.get(payment.booking_id)?.id !== payment.id) continue;
      items.push({
        id: `payment:${payment.id}`,
        kind: "payment",
        title: payment.status === "payment_failed" ? "Payment needs retrying" : "Payment needed",
        detail: payment.customer_total_pence
          ? `Protected payment · £${(payment.customer_total_pence / 100).toFixed(2)}`
          : "Protected payment",
        action: payment.status === "payment_failed" ? "Retry payment" : "Pay securely",
        to: `/bookings/${payment.booking_id}/payment`,
        priority: payment.status === "payment_failed" ? 8 : 15,
      });
    }

    for (const reminder of reminders ?? []) {
      items.push({
        id: `reminder:${reminder.id}`,
        kind: "reminder",
        title: reminder.title,
        detail: `Due ${new Date(reminder.due_date + "T12:00:00").toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`,
        action: "Open My Home",
        to: "/home",
        priority: 30,
      });
    }

    return items.sort((a, b) => a.priority - b.priority).slice(0, 8);
  });

export const listMyBookings = createServerFn({ method: "GET" }).middleware([requireSupabaseAuth]).handler(async ({ context }) => { const { data, error } = await context.supabase.from("bookings").select("id, reference, status, description, postcode, preferred_date, scheduled_date, scheduled_window, agreed_price_pence, created_at, services(name)").eq("customer_id", context.userId).order("created_at", { ascending: false }).limit(50); if (error) throw new Error(error.message); return data ?? []; });

export const getBookingDetail = createServerFn({ method: "POST" }).middleware([requireSupabaseAuth]).inputValidator((d: unknown) => z.object({ bookingId: z.string().uuid() }).parse(d)).handler(async ({ data, context }) => {
  const { data: booking, error } = await context.supabase.from("bookings").select("*, services(name)").eq("id", data.bookingId).eq("customer_id", context.userId).maybeSingle(); if (error) throw new Error(error.message); if (!booking) return null;
  const [{ data: bids }, { data: events }, { data: quotePartners }, media] = await Promise.all([
    context.supabase.from("bids").select("id, partner_id, price_pence, included, exclusions, earliest_date, duration_text, message, status, expires_at, created_at, credits_bid, price_source, price_approved").eq("booking_id", data.bookingId).order("price_pence"),
    context.supabase.from("booking_events").select("id, event_type, detail, created_at").eq("booking_id", data.bookingId).order("created_at", { ascending: false }).limit(30),
    context.supabase.from("job_quotes").select("partner_id").eq("booking_id", data.bookingId),
    signedBookingEvidence(data.bookingId),
  ]);
  return { booking, bids: bids ?? [], events: events ?? [], quotePartnerIds: [...new Set((quotePartners ?? []).map((quote) => quote.partner_id))], media };
});

export const acceptBid = createServerFn({ method: "POST" }).middleware([requireSupabaseAuth]).inputValidator((d: unknown) => z.object({ bidId: z.string().uuid() }).parse(d)).handler(async ({ data, context }) => { const { data: result, error } = await context.supabase.rpc("accept_bid", { p_bid_id: data.bidId }); if (error) return { ok: false as const, error: error.message }; return result as { ok: boolean; error?: string; booking_id?: string; credits_charged?: number }; });
export const approveBidPrice = createServerFn({ method: "POST" }).middleware([requireSupabaseAuth]).inputValidator((d: unknown) => z.object({ bidId: z.string().uuid() }).parse(d)).handler(async ({ data, context }) => { const { data: result, error } = await context.supabase.rpc("approve_bid_price", { p_bid_id: data.bidId }); if (error) return { ok: false as const, error: error.message }; return result as { ok: boolean; error?: string }; });

export const reportIssue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    bookingId: z.string().uuid(),
    category: z.string().min(2).max(80),
    description: z.string().min(10).max(4000),
    requestedOutcome: z.string().max(500).optional().nullable(),
  }).parse(d))
  .handler(async ({ data, context }) => {
    // The database locks the owned booking and commits the issue, status,
    // event and operations alert together. A retry returns the existing issue.
    const { data: result, error } = await context.supabase.rpc("report_booking_issue_authoritative", {
      p_booking_id: data.bookingId,
      p_category: data.category,
      p_description: data.description,
      ...(data.requestedOutcome != null ? { p_requested_outcome: data.requestedOutcome } : {}),
    });
    if (error) return { ok: false as const, error: error.message };
    return result as { ok: boolean; issueId?: string; duplicate?: boolean; error?: string };
  });

export const approveCompletion = createServerFn({ method: "POST" }).middleware([requireSupabaseAuth]).inputValidator((d: unknown) => z.object({ bookingId: z.string().uuid() }).parse(d)).handler(async ({ data, context }) => {
  const { supabase, userId } = context; const { data: booking } = await supabase.from("bookings").select("id, status, customer_id, assigned_partner_id, reference").eq("id", data.bookingId).maybeSingle(); if (!booking || booking.customer_id !== userId) return { ok: false as const, error: "Not found" }; if (booking.status !== "awaiting_customer_approval") return { ok: false as const, error: "This job is not awaiting approval" };
  const completedAt = new Date().toISOString();
  const { data: completedBooking, error } = await supabase.from("bookings").update({ status: "completed", customer_approved_at: completedAt, completed_at: completedAt }).eq("id", data.bookingId).eq("customer_id", userId).eq("status", "awaiting_customer_approval").select("id").maybeSingle(); if (error) return { ok: false as const, error: error.message }; if (!completedBooking) return { ok: false as const, error: "This job is no longer awaiting approval" };
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server"); await supabaseAdmin.from("booking_events").insert({ booking_id: data.bookingId, actor_id: userId, event_type: "customer_approved_completion", detail: {} }); const { checkCustomerAchievements } = await import("./achievements.server"); await checkCustomerAchievements(userId);
  if (booking.assigned_partner_id) { const { checkJobAchievements } = await import("./achievements.server"); await checkJobAchievements(booking.assigned_partner_id); await sendNotification(supabaseAdmin, { recipientId: booking.assigned_partner_id, template: "job_completed", payload: { booking_id: booking.id, reference: booking.reference } }); }
  const { releaseJobPaymentForCompletedBooking } = await import("./job-payments.server"); const release = await releaseJobPaymentForCompletedBooking(booking.id); return { ok: true as const, paymentRelease: release.ok ? "released" as const : "pending" as const, paymentReleaseError: release.ok ? undefined : release.error };
});
